import {
  ChangeEvent,
  ChangeEventHandler,
  memo,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";

import styled from "styled-components";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { useUserStore } from "stores/UserStore";

import useAnalytics from "effect/use_analytics";

// -----------------------------------------------------------------------------

const AccountPage = memo(function AccountPage(): JSX.Element {
  useAnalytics("Account: Account Settings");

  //
  const [userStore, userActions] = useUserStore();
  const user = userStore.user;

  //
  const [expanded, setExpanded] = useState(false);
  const [expandedActivity, setExpandedActivity] = useState(false);

  const [preferredName, setPreferredName] = useState(user.preferredName);
  const [fullName, setFullName] = useState(user.fullName);
  const [gravatarEmail, setGravatarEmail] = useState(user.gravatarEmail);

  const [productAndFeatures, setProductAndFeatures] = useState(
    !(user.preferences.productAndFeatures == null)
  );
  const [companyAnnouncements, setCompanyAnnouncements] = useState(
    !(user.preferences.companyAnnouncements == null)
  );
  const [educationAndTraining, setEducationAndTraining] = useState(
    !(user.preferences.educationAndTraining == null)
  );
  const [monthlyNewsletter, setMonthlyNewsletter] = useState(
    !(user.preferences.monthlyNewsletter == null)
  );
  const [sourceConnected, setSourceConnected] = useState(
    !(user.preferences.sourceConnected == null)
  );
  const [sourceDisconnected, setSourceDisconnected] = useState(
    !(user.preferences.sourceDisconnected == null)
  );
  const [sourceScanFailure, setSourceScanFailure] = useState(
    !(user.preferences.scanFailure == null)
  );
  const [destinationConnected, setDestinationConnected] = useState(
    !(user.preferences.destinationConnected == null)
  );
  const [destinationDisconnected, setDestinationDisconnected] = useState(
    !(user.preferences.destinationDisconnected == null)
  );

  //
  useEffect(() => {
    if (
      userStore.savedEdit.success === null ||
      userStore.savedEdit.success === undefined
    ) {
      return;
    }

    userStore.savedEdit.success
      ? toast.success("Settings saved", { position: "bottom-center" })
      : toast.error(
          `Failed to save settings. ${userStore.savedEdit.errorMsg}`,
          {
            position: "bottom-center",
          }
        );

    userActions.clearSavedEdit();
  }, [userActions, userStore.savedEdit]);

  //
  function toggleExpanded() {
    setExpanded((expanded) => !expanded);
  }

  function toggleExpandedActivity() {
    setExpandedActivity((expandedActivity) => !expandedActivity);
  }

  function setField(set: Function): ChangeEventHandler<HTMLInputElement> {
    return (e: ChangeEvent<HTMLInputElement>) => set(e.target.value);
  }

  function toggleField(set: Function): ChangeEventHandler<HTMLInputElement> {
    return (e: ChangeEvent<HTMLInputElement>) =>
      set(e.target.checked ? new Date().toISOString() : null);
  }

  function resetForm() {
    setPreferredName(user.preferredName);
    setFullName(user.fullName);
    setGravatarEmail(user.gravatarEmail);
    setProductAndFeatures(!(user.preferences.productAndFeatures == null));
    setCompanyAnnouncements(!(user.preferences.companyAnnouncements == null));
    setEducationAndTraining(!(user.preferences.educationAndTraining == null));
    setMonthlyNewsletter(!(user.preferences.monthlyNewsletter == null));
    setSourceConnected(!(user.preferences.sourceConnected == null));
    setSourceDisconnected(!(user.preferences.sourceDisconnected == null));
    setSourceScanFailure(!(user.preferences.scanFailure == null));
    setDestinationConnected(!(user.preferences.destinationConnected == null));
    setDestinationDisconnected(
      !(user.preferences.destinationDisconnected == null)
    );
  }

  function submitForm() {
    toast.dismiss();
    userActions.edit({
      preferred_name: preferredName,
      full_name: fullName,
      gravatar_specific_email: gravatarEmail,
      product_and_features: dateOrNull(productAndFeatures),
      company_announcements: dateOrNull(companyAnnouncements),
      education_and_training: dateOrNull(educationAndTraining),
      monthly_newsletter: dateOrNull(monthlyNewsletter),
      source_connected: dateOrNull(sourceConnected),
      source_disconnected: dateOrNull(sourceDisconnected),
      source_scan_failure: dateOrNull(sourceScanFailure),
      destination_connected: dateOrNull(destinationConnected),
      destination_disconnected: dateOrNull(destinationDisconnected),
    });
  }

  //
  return (
    <>
      <Typography variant="h4">Account Settings</Typography>
      <Box sx={{ mt: 2 }}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            General Settings
          </Typography>

          <TextFieldStyled
            fullWidth
            label="Email Address"
            variant="outlined"
            disabled
            defaultValue={user.email}
          />

          <TextFieldStyled
            fullWidth
            label="Preferred Name"
            variant="outlined"
            value={preferredName}
            onChange={setField(setPreferredName)}
          />

          <TextFieldStyled
            fullWidth
            label="Full Name"
            variant="outlined"
            value={fullName}
            onChange={setField(setFullName)}
          />

          <TextFieldStyled
            fullWidth
            label="Gravatar Email (Private)"
            variant="outlined"
            value={gravatarEmail}
            onChange={setField(setGravatarEmail)}
          />
        </Paper>

        <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={toggleExpandedActivity}
          >
            <ArrowForwardIosIcon
              sx={{
                fontSize: "1.25rem",
                mr: 1,
                transform: expandedActivity ? "rotate(90deg)" : undefined,
                transition: "all 250ms",
              }}
            />
            <Typography variant="h6">Activity Notifications</Typography>
          </Box>

          <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
            Get alerted when events happen within the ThreatKey Platform.
            Receive emails for activity across your ThreatKey workspace.
          </Typography>

          <Collapse in={expandedActivity} timeout="auto" unmountOnExit>
            <FormGroup>
              <FormControlLabel
                label="Source Connected"
                control={
                  <Switch
                    checked={sourceConnected}
                    onChange={toggleField(setSourceConnected)}
                  />
                }
              />

              <FormControlLabel
                label="Source Disconnected"
                control={
                  <Switch
                    checked={sourceDisconnected}
                    onChange={toggleField(setSourceDisconnected)}
                  />
                }
              />

              <FormControlLabel
                label="Source is Unhealthy"
                control={
                  <Switch
                    checked={sourceScanFailure}
                    onChange={toggleField(setSourceScanFailure)}
                  />
                }
              />

              <FormControlLabel
                label="Destination Connected"
                control={
                  <Switch
                    name="destination_connected"
                    checked={destinationConnected}
                    onChange={toggleField(setDestinationConnected)}
                  />
                }
              />

              <FormControlLabel
                label="Destination Disconnected"
                control={
                  <Switch
                    name="destination_disconnected"
                    checked={destinationDisconnected}
                    onChange={toggleField(setDestinationDisconnected)}
                  />
                }
              />
            </FormGroup>
          </Collapse>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={toggleExpanded}
          >
            <ArrowForwardIosIcon
              sx={{
                fontSize: "1.25rem",
                mr: 1,
                transform: expanded ? "rotate(90deg)" : undefined,
                transition: "all 250ms",
              }}
            />
            <Typography variant="h6">Email Subscriptions</Typography>
          </Box>

          <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
            Enable your email subscriptions to get the latest news, new
            features, and product updates from ThreatKey. You can unsubscribe at
            any time.
          </Typography>

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <FormGroup>
              <FormControlLabel
                label="Product and Feature Updates"
                control={
                  <Switch
                    checked={productAndFeatures}
                    onChange={toggleField(setProductAndFeatures)}
                  />
                }
              />

              <FormControlLabel
                label="Company Announcements"
                control={
                  <Switch
                    checked={companyAnnouncements}
                    onChange={toggleField(setCompanyAnnouncements)}
                  />
                }
              />

              <FormControlLabel
                label="Education and Training"
                control={
                  <Switch
                    checked={educationAndTraining}
                    onChange={toggleField(setEducationAndTraining)}
                  />
                }
              />

              <FormControlLabel
                label="Monthly Newsletter"
                control={
                  <Switch
                    name="monthly_newsletter"
                    checked={monthlyNewsletter}
                    onChange={toggleField(setMonthlyNewsletter)}
                  />
                }
              />
            </FormGroup>
          </Collapse>
        </Paper>
      </Box>
      <Box sx={{ mt: 3 }}>
        <ButtonStyled variant="contained" onClick={submitForm}>
          Save Changes
        </ButtonStyled>
        <ButtonStyled variant="outlined" onClick={resetForm}>
          Reset
        </ButtonStyled>
      </Box>
    </>
  );
});

export default AccountPage;

// -----------------------------------------------------------------------------

function dateOrNull(value: boolean): Date | null {
  return value ? new Date() : null;
}

// -----------------------------------------------------------------------------

const TextFieldStyled = styled(TextField)`
  & + & {
    margin-top: 16px;
  }
`;

const ButtonStyled = styled(Button)`
  & + & {
    margin-left: 16px;
  }
`;
